// Jedyne źródło prawdy: typ obiektu z ARON-a → trasa w aplikacji.
//
// Dwa poziomy, bo tyle da się uczciwie obiecać:
//   • ENTITY_ROUTE      — obiekt MA własną stronę; odsyłacz prowadzi wprost do niego.
//   • ENTITY_MODULE     — własnej strony nie ma; odsyłacz prowadzi do modułu, w którym
//                         ten obiekt żyje. Gorsze niż strona obiektu, ale nieporównanie
//                         lepsze niż goły tekst „utworzyłem X" bez możliwości kliknięcia.
//
// Trasy nie są sprawdzane na oko: `entityRoute.test.ts` przepuszcza KAŻDĄ pozycję obu
// map przez prawdziwą mapę tras aplikacji, więc usunięcie albo przeniesienie ekranu
// wywala test, a nie użytkownika na pustą stronę.
//
// Nazwy typów po lewej MUSZĄ zgadzać się z `apps.aron.services.catalog.SEMANTIC`
// (pole `entity`) — to ta tabela stempluje karty zapisu i odsyłacze `aron-ref:`.
const ENTITY_ROUTE: Record<string, (id: string) => string> = {
  Order: (id) => `/orders/${id}`,
  Requisition: (id) => `/requisitions/${id}`,
  Project: (id) => `/projects/${id}`,
  AcceptanceProtocol: (id) => `/protocols/${id}`,
  ContractorInvoice: (id) => `/invoices/${id}`,
  ClientInvoice: (id) => `/finances/invoices-prz/${id}`,
  // EmployeeExpense (faktura kosztowa pracownika) nie ma strony szczegółów — deep-link do
  // listy z podświetleniem wiersza. Lista jest scoped po stronie serwera, więc id spoza
  // zakresu użytkownika po prostu się nie podświetli (bezpieczny no-op, brak wycieku).
  EmployeeExpense: (id) => `/expenses?expense=${id}`,
  TechnicalIssue: (id) => `/dev-issues/${id}`,
  Contractor: (id) => `/contractors/${id}`,
  FrameworkAgreement: (id) => `/agreements/${id}`,
  Tender: (id) => `/tenders/${id}`,
  OfficialFeeRequest: (id) => `/official-fees/${id}`,
  RfqProceeding: (id) => `/rfq/proceedings/${id}`,
  B2BEmployeeInvoice: (id) => `/b2b-invoices/${id}`,
  Client: (id) => `/clients/${id}`,
  Estimate: (id) => `/estimating/${id}`,
  EstimateJob: (id) => `/estimating/${id}`,
  EstimatingJob: (id) => `/estimating/${id}`,
  DesignReview: (id) => `/eng-aron/reviews/${id}`,
  // Świat DLC_01 (Przedsięwzięcia) — najczęściej dotykany przez tryb samodzielny.
  // Id PROFILU ZE, a /power-projects/:id kluczuje po id przedsięwzięcia — „profil/" przekierowuje.
  ZEShortTypeProject: (id) => `/power-projects/profil/${id}`,
  WypisOrder: (id) => `/power-projects/wypisy/${id}`,
  MzOrder: (id) => `/power-projects/mz/${id}`,
  MpzpOrder: (id) => `/power-projects/mpzp/${id}`,
  OfficialApplication: (id) => `/power-projects/wnioski/${id}`,
};

/** Typ bez własnej strony → moduł, w którym mieszka. */
const ENTITY_MODULE: Record<string, string> = {
  Car: "/fleet",
  CampaignMessage: "/leads/campaigns",
  Cashflow: "/finances",
  CoefficientSet: "/estimating",
  Company: "/group-finance",
  CompanyBalance: "/group-finance",
  ContractorCategoryQualification: "/contractors",
  ContractorRating: "/contractors",
  CooperationScope: "/rfq/categories",
  CustomPrice: "/estimating/custom-prices",
  DesignKnowledgeEntry: "/eng-aron/knowledge",
  DesignReviewFinding: "/eng-aron/findings",
  DocTemplate: "/doc-generation",
  DxfLayerPreset: "/power-projects/presety",
  EmployeeSalaryCost: "/finances/salary",
  EstimatePosition: "/estimating",
  InternalCost: "/finances/costs",
  InternalCostRecurring: "/finances/costs",
  KnrNormPosition: "/estimating/catalogs",
  LeaveRequest: "/leaves",
  LineItemType: "/agreements",
  MetadataPreset: "/power-projects/presety?tab=metadane",
  PlannedRevenue: "/finances/planned-revenues",
  ProjectContact: "/power-projects/contacts",
  ProjectMessage: "/projects",
  RateCatalog: "/tenders/pricing-catalog",
  ScopeCategory: "/rfq/categories",
  ScopeExtensionRequest: "/rfq/scope-requests",
  Task: "/tasks/list",
  TenderOffer: "/tenders",
  TenderPricingProposal: "/tenders",
  User: "/employees",
  VATEntry: "/group-finance",
  WodyOrder: "/power-projects",
  WpwFieldReturnBatch: "/power-projects",
  WpwOwnerHandover: "/przekazanie-obowiazkow",
  Lead: "/leads",
};

export function entityRoute(entity: string, id: string): string | null {
  if (!id || !(entity in ENTITY_ROUTE)) return null;
  return ENTITY_ROUTE[entity](id);
}

/** Trasa do modułu obiektu, gdy własnej strony nie ma. Bez id — to nie deep-link. */
export function entityModuleRoute(entity: string): string | null {
  return ENTITY_MODULE[entity] ?? null;
}

/** Strona obiektu, a gdy jej nie ma — jego moduł. `kind` mówi, co dostaliśmy,
 *  żeby etykieta nie obiecywała „otwórz fakturę", prowadząc na listę. */
export function entityLink(
  entity: string,
  id: string,
): { route: string; kind: "detail" | "module" } | null {
  const detail = entityRoute(entity, id);
  if (detail) return { route: detail, kind: "detail" };
  const modul = entityModuleRoute(entity);
  return modul ? { route: modul, kind: "module" } : null;
}

export const TYPY_ZE_STRONA = Object.keys(ENTITY_ROUTE);
export const TYPY_Z_MODULEM = Object.keys(ENTITY_MODULE);
